import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Button, ButtonGroup } from "react-bootstrap";

import {
  YoutubeContentApiResponse,
  YoutubeContentUpsertRequest,
  fetchYoutubeContent,
  upsertYoutubeContent,
  deleteYoutubeContent,
  addPositionsToYoutubeContent,
  removePositionsFromYoutubeContent,
  upsertYoutubeContentThumbnail,
} from "../../../../api/youtubeContentApi";
import { fetchCategory, CategoryApiResponse } from "../../../../api/categoryApi";
import { fetchSubCategory, SubCategoryApiResponse } from "../../../../api/subCategoryApi";
import { fetchTag, TagApiResponse } from "../../../../api/tagApi";
import { fetchSubTag, SubTagApiResponse } from "../../../../api/subTagApi";
import { fetchPosition, PositionApiResponse } from "../../../../api/positionApi";
import { uploadImage } from "../../../../api/imageUploadApi";
import { PAGE_TITLE, OPERATION_NAME } from "../../../../constants";
import { Operation } from "../../../../types";
import { TableContent, Table, TdLong } from "../..";
import { ReferenceApiResponse, fetchReference } from "../../../../api/referenceApi";
import { AffiliateApiResponse, fetchAffiliate } from "../../../../api/affiliateApi";

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
`;

type YoutubeContentEditFormData = YoutubeContentUpsertRequest & {
  thumbnail?: string | null;
  positions?: PositionApiResponse[] | null;
};

const ContentsList = () => {
  const [contentData, setContentData] = useState<YoutubeContentApiResponse[]>([]);
  const [categoryData, setCategoryData] = useState<CategoryApiResponse[]>([]);
  const [subCategoryData, setSubCategoryData] = useState<SubCategoryApiResponse[]>([]);
  const [tagData, setTagData] = useState<TagApiResponse[]>([]);
  const [subTagData, setSubTagData] = useState<SubTagApiResponse[]>([]);
  const [positionData, setPositionData] = useState<PositionApiResponse[]>([]);
  const [referenceData, setReferenceData] = useState<ReferenceApiResponse[]>([]);
  const [affiliateData, setAffiliateData] = useState<AffiliateApiResponse[]>([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [operationName, setOperationName] = useState<Operation | null>(null);
  const [editFormData, setEditFormData] = useState<YoutubeContentEditFormData | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string | null>(null);
  const [thumbnailData, setThumbnailData] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | ArrayBuffer | null>(null);
  const [previousOrderIndex, setPreviousOrderIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchYoutubeContent().then((contentData) => {
      setContentData(contentData);
    });
    fetchCategory().then((categoryData) => {
      setCategoryData(categoryData);
    });
    fetchSubCategory().then((subCategoryData) => {
      setSubCategoryData(subCategoryData);
    });
    fetchTag().then((tagData) => {
      setTagData(tagData);
    });
    fetchSubTag().then((subTagData) => {
      setSubTagData(subTagData);
    });
    fetchPosition().then((positionData) => {
      setPositionData(positionData);
    });
    fetchReference().then((referenceData) => {
      setReferenceData(referenceData);
    });
    fetchAffiliate().then((affiliateData) => {
      setAffiliateData(affiliateData);
    });
  }, [operationName]);

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const newItemData: YoutubeContentUpsertRequest = {
      contentName: formData.get("contentName") as string,
      url: formData.get("url") as string,
      summary: formData.get("summary") as string,
      orderIndex: Number(formData.get("orderIndex") as string) || null,
      categoryId: (formData.get("categoryId") as string) || null,
      subCategoryId: (formData.get("subCategoryId") as string) || null,
      tagId: (formData.get("tagId") as string) || null,
      subTagId: (formData.get("subTagId") as string) || null,
      referenceId: (formData.get("referenceId") as string) || null,
      affiliateId: (formData.get("affiliateId") as string) || null,
    };

    try {
      const newItem = await upsertYoutubeContent(newItemData);
      if (!newItem) return;

      const youtubeContentId = newItem.id;
      const linkedPositionIds = formData.getAll("positions") as string[];
      await addPositionsToYoutubeContent({ id: youtubeContentId, positionIds: linkedPositionIds });

      if (thumbnailData) {
        const thumbnailUrl = await uploadImage({ file: thumbnailData, path: "thumbnails" });
        if (thumbnailUrl) {
          await upsertYoutubeContentThumbnail({ id: youtubeContentId, thumbnail: thumbnailUrl });
        }
      }

      closeModal();
    } catch (error) {
      alert("Create error: " + error);
    }
  };

  const handleEditClick = (item: YoutubeContentEditFormData) => {
    openModal(OPERATION_NAME.EDIT);
    setEditFormData({
      id: item.id,
      contentName: item.contentName,
      url: item.url,
      summary: item.summary,
      thumbnail: item.thumbnail,
      orderIndex: item.orderIndex,
      categoryId: item.categoryId,
      subCategoryId: item.subCategoryId,
      tagId: item.tagId,
      subTagId: item.subTagId,
      positions: item.positions,
      referenceId: item.referenceId,
      affiliateId: item.affiliateId,
    });
    setPreviousOrderIndex(item.orderIndex);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    event.preventDefault();
    if (!editFormData) return;

    const editItemData: YoutubeContentEditFormData = {
      ...editFormData,
      // NOTE: falsey な値を null に変換
      orderIndex: editFormData.orderIndex || null,
    };

    try {
      const updatedItem = await upsertYoutubeContent(editItemData);
      if (updatedItem) {
        const youtubeContentId = editItemData.id;
        if (!youtubeContentId) return;
        const linkedPositionIds = editItemData.positions?.map((position) => position.id) ?? [];

        const deleteResult = await removePositionsFromYoutubeContent({
          id: youtubeContentId,
        });

        if (deleteResult && linkedPositionIds.length > 0) {
          await addPositionsToYoutubeContent({
            id: youtubeContentId,
            positionIds: linkedPositionIds,
          });
        }

        if (thumbnailData) {
          const thumbnailUrl = await uploadImage({ file: thumbnailData, path: "thumbnails" });
          if (thumbnailUrl) {
            await upsertYoutubeContentThumbnail({ id: youtubeContentId, thumbnail: thumbnailUrl });
          }
        }
      }

      setEditFormData(null);
      closeModal();
    } catch (error) {
      alert("Update error: " + error);
    }
  };

  const handleDeleteClick = async (id: string) => {
    setOperationName(OPERATION_NAME.DELETE);

    try {
      if (confirm("Are you sure you want to delete this item?")) {
        await deleteYoutubeContent({ id });
      }
    } catch (error) {
      alert("Delete error: " + error);
    }

    setOperationName(null);
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // NOTE: フォームのデフォルト動作を防止
    event.preventDefault();

    const reader = new FileReader();
    const file = event.target.files?.[0] || null;
    if (file) {
      setThumbnailData(file);
      reader.onloadend = () => {
        setThumbnailPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openModal = (operation: Operation) => {
    setOperationName(operation);
    setShowModal(true);
  };

  const closeModal = () => {
    setOperationName(null);
    setThumbnailData(null);
    setThumbnailPreviewUrl(null);
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setShowModal(false);
  };

  return (
    <>
      <h2>{PAGE_TITLE.ContentsListPage}</h2>
      <div className="my-3">
        <Button variant="primary" onClick={() => openModal(OPERATION_NAME.CREATE)}>
          新規作成
        </Button>
      </div>
      <TableContent>
        <Table>
          <thead>
            <tr>
              <th>Thumbnail</th>
              <th>Content Name</th>
              <th>Category</th>
              <th>Sub Category</th>
              <th>Order Index</th>
              <th>Tag</th>
              <th>Sub Tag</th>
              <th>Positions</th>
              <th>URL</th>
              <th>Summary</th>
              <th>Reference</th>
              <th>Affiliate</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const sortedContents = contentData.sort((a, b) => {
                if (a.category && b.category) {
                  const orderA = a.category.orderIndex !== null ? a.category.orderIndex : Infinity;
                  const orderB = b.category.orderIndex !== null ? b.category.orderIndex : Infinity;
                  if (orderA !== orderB) {
                    return orderA - orderB;
                  }
                } else if (!a.category && b.category) {
                  return 1;
                } else if (a.category && !b.category) {
                  return -1;
                }

                if (a.subCategory && b.subCategory) {
                  const orderA =
                    a.subCategory.orderIndex !== null ? a.subCategory.orderIndex : Infinity;
                  const orderB =
                    b.subCategory.orderIndex !== null ? b.subCategory.orderIndex : Infinity;
                  if (orderA !== orderB) {
                    return orderA - orderB;
                  }
                } else if (!a.subCategory && b.subCategory) {
                  return 1;
                } else if (a.subCategory && !b.subCategory) {
                  return -1;
                }

                const orderA = a.orderIndex ? a.orderIndex : Infinity;
                const orderB = b.orderIndex ? b.orderIndex : Infinity;
                return orderA - orderB;
              });

              return sortedContents.map((item) => (
                <tr key={item.id}>
                  <td>
                    <img src={item.thumbnail || "/noImage.jpg"} width={100} />
                  </td>
                  <TdLong>{item.contentName}</TdLong>
                  <td>{item.category?.categoryName}</td>
                  <td>{item.subCategory?.subCategoryName}</td>
                  <td>{item.orderIndex || "--"}</td>
                  <td>{item.tag?.tagName}</td>
                  <td>{item.subTag?.subTagName}</td>
                  <td>{item.positions?.map((position) => "[" + position.positionName + "]")}</td>
                  <TdLong>
                    <a href={item.url} target="_blank">
                      {item.url}
                    </a>
                  </TdLong>
                  <TdLong>{item.summary}</TdLong>
                  <TdLong>
                    {item.reference?.chName + " "}
                    <a href={item.reference?.chUrl} target="_blank">
                      {item.reference?.chUrl}
                    </a>
                  </TdLong>
                  <TdLong>{item.affiliate?.affiliateName}</TdLong>
                  <td className="text-center">
                    {new Date(item.createdAt || "").toLocaleDateString()}
                  </td>
                  <td className="text-center">
                    {new Date(item.updatedAt || "").toLocaleDateString()}
                  </td>
                  <td className="text-center">
                    <ButtonGroup>
                      <Button variant="primary" onClick={() => handleEditClick(item)}>
                        編集
                      </Button>
                      <Button variant="secondary" onClick={() => handleDeleteClick(item.id)}>
                        削除
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </Table>
      </TableContent>
      {showModal && operationName === OPERATION_NAME.CREATE && (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleCreate}>
              <input type="text" name="contentName" placeholder="Content Name" required />
              <select name="categoryId" onChange={(e) => setSelectedCategoryId(e.target.value)}>
                <option value="">Categoryを選択</option>
                {categoryData.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
              <select
                name="subCategoryId"
                onChange={(e) => setSelectedSubCategoryId(e.target.value)}
              >
                <option value="">Sub Categoryを選択</option>
                {subCategoryData
                  .filter(({ categoryId }) => categoryId === selectedCategoryId)
                  .map((subCategory) => (
                    <option key={subCategory.id} value={subCategory.id}>
                      {subCategory.subCategoryName}
                    </option>
                  ))}
              </select>
              <select name="orderIndex">
                <option value="">表示順を選択</option>
                {selectedSubCategoryId &&
                  (() => {
                    const filteredContents = contentData.filter(
                      (content) =>
                        content.subCategoryId === selectedSubCategoryId &&
                        content.orderIndex !== null,
                    );

                    const options = filteredContents.map((content) => (
                      <option key={content.id} value={content.orderIndex}>
                        {content.orderIndex}
                      </option>
                    ));

                    options.push(
                      <option key="new" value={filteredContents.length + 1}>
                        {filteredContents.length + 1}
                      </option>,
                    );

                    return options;
                  })()}
              </select>
              <select name="tagId">
                <option value="">Tagを選択</option>
                {tagData.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.tagName}
                  </option>
                ))}
              </select>
              <select name="subTagId">
                <option value="">Sub Tagを選択</option>
                {subTagData.map((subTag) => (
                  <option key={subTag.id} value={subTag.id}>
                    {subTag.subTagName}
                  </option>
                ))}
              </select>
              <select name="positions" multiple>
                <option value="">Positionを選択</option>
                {positionData.map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.positionName}
                  </option>
                ))}
              </select>
              <select>
                <option value="">Referenceを選択</option>
                {referenceData.map((reference) => (
                  <option key={reference.id} value={reference.id}>
                    {reference.chName}
                  </option>
                ))}
              </select>
              <select>
                <option value="">Affiliateを選択</option>
                {affiliateData.map((affiliate) => (
                  <option key={affiliate.id} value={affiliate.id}>
                    {affiliate.affiliateName}
                  </option>
                ))}
              </select>
              <input type="text" name="url" placeholder="URL" />
              <input type="text" name="summary" placeholder="Summary" />
              <button type="submit">Save</button>
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
              <input type="file" name="thumbnail" onChange={handleThumbnailChange} />
              {thumbnailPreviewUrl && <img src={thumbnailPreviewUrl.toString()} width={100} />}
            </form>
          </ModalContent>
        </ModalBackdrop>
      )}
      {showModal && operationName === OPERATION_NAME.EDIT && editFormData && (
        <ModalBackdrop>
          <ModalContent>
            <form onSubmit={handleSave}>
              <input
                type="text"
                name="contentName"
                placeholder="Content Name"
                value={editFormData.contentName || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    contentName: e.target.value,
                  })
                }
                required
              />
              <select
                name="categoryId"
                value={editFormData.categoryId || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    categoryId: e.target.value,
                    subCategoryId: "",
                  })
                }
              >
                <option value="">Categoryを選択</option>
                {categoryData.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
              <select
                name="subCategoryId"
                value={editFormData.subCategoryId || ""}
                onChange={(e) => {
                  setEditFormData({
                    ...editFormData,
                    subCategoryId: e.target.value,
                  });
                }}
              >
                <option value="">Sub Categoryを選択</option>
                {subCategoryData
                  .filter(({ categoryId }) => categoryId === editFormData.categoryId)
                  .map((subCategory) => (
                    <option key={subCategory.id} value={subCategory.id}>
                      {subCategory.subCategoryName}
                    </option>
                  ))}
              </select>
              <select
                name="orderIndex"
                value={editFormData.orderIndex || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    orderIndex: Number(e.target.value),
                  })
                }
              >
                <option value="">表示順を選択</option>
                {editFormData.subCategoryId &&
                  (() => {
                    const filteredContents = contentData.filter(
                      (content) =>
                        content.subCategoryId === editFormData.subCategoryId &&
                        content.orderIndex !== null,
                    );

                    const options = filteredContents.map((content) => {
                      return (
                        <option key={content.id} value={content.orderIndex}>
                          {content.orderIndex}
                        </option>
                      );
                    });

                    if (previousOrderIndex === null) {
                      options.push(
                        <option key={editFormData.id} value={filteredContents.length + 1}>
                          {filteredContents.length + 1}
                        </option>,
                      );
                    }

                    return options;
                  })()}
              </select>
              <select
                name="tagId"
                value={editFormData.tagId || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    tagId: e.target.value,
                  })
                }
              >
                <option value="">Tagを選択</option>
                {tagData.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.tagName}
                  </option>
                ))}
              </select>
              <select
                name="subTagId"
                value={editFormData.subTagId || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    subTagId: e.target.value,
                  })
                }
              >
                <option value="">Sub Tagを選択</option>
                {subTagData.map((subTag) => (
                  <option key={subTag.id} value={subTag.id}>
                    {subTag.subTagName}
                  </option>
                ))}
              </select>
              <select
                name="positions"
                defaultValue={editFormData.positions?.map((position) => position.id) || []}
                multiple
                onChange={(e) => {
                  const selectedIds = Array.from(
                    e.target.selectedOptions,
                    (option) => option.value,
                  );
                  const selectedPositions = positionData.filter((position) =>
                    selectedIds.includes(position.id),
                  );

                  setEditFormData({
                    ...editFormData,
                    positions: selectedPositions,
                  });
                }}
              >
                <option value="">Positionを選択</option>
                {positionData.map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.positionName}
                  </option>
                ))}
              </select>
              <select
                name="referenceId"
                value={editFormData.referenceId || ""}
                onChange={(e) => setEditFormData({ ...editFormData, referenceId: e.target.value })}
              >
                <option value="">Referenceを選択</option>
                {referenceData.map((reference) => (
                  <option key={reference.id} value={reference.id}>
                    {reference.chName}
                  </option>
                ))}
              </select>
              <select
                name="affiliateId"
                value={editFormData.affiliateId || ""}
                onChange={(e) => setEditFormData({ ...editFormData, affiliateId: e.target.value })}
              >
                <option value="">Affiliateを選択</option>
                {affiliateData.map((affiliate) => (
                  <option key={affiliate.id} value={affiliate.id}>
                    {affiliate.affiliateName}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="url"
                placeholder="URL"
                value={editFormData.url || ""}
                onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
              />
              <input
                type="text"
                name="summary"
                placeholder="Summary"
                value={editFormData.summary || ""}
                onChange={(e) => setEditFormData({ ...editFormData, summary: e.target.value })}
              />
              <button type="submit">Save</button>
              <button
                type="button"
                onClick={() => {
                  setEditFormData(null);
                  closeModal();
                }}
              >
                Cancel
              </button>
              <input type="file" name="thumbnail" onChange={handleThumbnailChange} />
              <img
                src={thumbnailPreviewUrl?.toString() || editFormData.thumbnail || ""}
                width={100}
              />
            </form>
          </ModalContent>
        </ModalBackdrop>
      )}
    </>
  );
};

export default ContentsList;
